/**
 * Helpers that turn the raw contact fields a business types during onboarding
 * into hrefs that actually go somewhere. The onboarding form collects social
 * accounts as handles ("@warungx" / "warungx") and the website as free text, so
 * binding them straight into `href` produced relative links such as
 * `/business-detail/123/warungx`.
 */

const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;

/** Website: adds https:// when no scheme was typed; '' when there is nothing usable. */
export function toWebsiteUrl(value: string | null | undefined): string {
  const text = (value ?? '').trim();
  if (!text) {
    return '';
  }
  return SCHEME.test(text) ? text : `https://${text.replace(/^\/+/, '')}`;
}

/**
 * Social account: a handle becomes `${profileBase}${handle}`; a pasted profile
 * URL is kept (scheme added if missing); '' when empty.
 */
export function toSocialUrl(value: string | null | undefined, profileBase: string): string {
  const text = (value ?? '').trim();
  if (!text) {
    return '';
  }
  if (SCHEME.test(text)) {
    return text;
  }
  // "instagram.com/warungx" or "www.x.com/warungx" typed without a scheme.
  if (/^(www\.)?(instagram\.com|twitter\.com|x\.com)\//i.test(text)) {
    return `https://${text}`;
  }
  const handle = text.replace(/^@+/, '').split(/[\s/?#]/)[0];
  return handle ? `${profileBase}${encodeURIComponent(handle)}` : '';
}

export const toInstagramUrl = (value: string | null | undefined) => toSocialUrl(value, 'https://www.instagram.com/');
export const toTwitterUrl = (value: string | null | undefined) => toSocialUrl(value, 'https://x.com/');

/** `tel:` href for a stored phone number, or '' when there is none. */
export function toTelUrl(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : '';
}
